import React, { useContext, useRef } from "react";
import TextareaAutosize from "react-textarea-autosize";
import cx from "classnames";
import { QuestionAnswer, ValidationResult } from "../../types/forms";
import ValidationWarning from "./ValidationWarning";
import SpeechToTextVoiceRow from "./SpeechToTextVoiceRow";
import TextArea from "./TextArea";
import UserContext from "../../state/UserContext";
import moduleAccessHelper from "../../helpers/moduleAccessHelper";
import ttModulesList from "../../config/modules/ttModulesList";

interface VoiceTextAreaProps {
  onChange(newValue: string): void;
  onBlur?(
    latestValue: string,
    nonStateValue?: QuestionAnswer | null,
  ): void | undefined;
  value: string | undefined;
  inputId?: string;
  shellClassName?: string;
  inputClassName?: string;
  actionLinkClassName?: string;
  statusTextClassName?: string;
  idleMicClassName?: string;
  errorAsBadge?: boolean;
  errorBadgeBackgroundColourClassName?: string;
  errorBadgeTextColourClassName?: string;
  errorBadgeBorderRadiusClassName?: string;
  minRows?: number | undefined;
  maxRows?: number | undefined;
  placeholder?: string | undefined;
  inputRef?: React.RefObject<HTMLTextAreaElement> | undefined;
  showValidationErrors?: boolean;
  validationResult?: ValidationResult | null;
  isReadOnly?: boolean;
  maxLength?: number | undefined;
  readonlyDisplayMode?: "DISABLED-INPUT" | "TEXT";
  readonlyTextModeEmptyPlaceholder?: string | undefined;
  readonlyTextModeCustomClassNames?: string | undefined;
}

const VoiceTextArea = ({
  onChange,
  onBlur = undefined,
  minRows,
  maxRows,
  showValidationErrors = false,
  validationResult = null,
  value = "",
  inputId = "",
  shellClassName = "",
  inputClassName = "",
  actionLinkClassName = undefined,
  statusTextClassName = undefined,
  idleMicClassName = undefined,
  errorAsBadge = false,
  errorBadgeBackgroundColourClassName = undefined,
  errorBadgeTextColourClassName = undefined,
  errorBadgeBorderRadiusClassName = undefined,
  placeholder = undefined,
  inputRef = undefined,
  isReadOnly = false,
  maxLength = undefined,
  readonlyDisplayMode = "DISABLED-INPUT",
  readonlyTextModeEmptyPlaceholder = undefined,
  readonlyTextModeCustomClassNames = undefined,
}: VoiceTextAreaProps) => {
  const userContext = useContext(UserContext);
  const internalRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = inputRef || internalRef;
  const voiceCursorRef = useRef(0);

  const hasAiSpeechToText = moduleAccessHelper.userHasModule(
    userContext.user,
    ttModulesList.Ai.SpeechToText,
  );

  const isSpeechRecognitionSupported =
    typeof window !== "undefined" &&
    navigator.mediaDevices &&
    navigator.mediaDevices.getUserMedia;

  const showVoice =
    hasAiSpeechToText && isSpeechRecognitionSupported && !isReadOnly;

  const syncVoiceCursor = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    voiceCursorRef.current = e.currentTarget.selectionStart || 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    if (onBlur) {
      onBlur(e.target.value);
    }
  };

  if (!showVoice) {
    return (
      <TextArea
        onChange={onChange}
        onBlur={onBlur}
        value={value}
        inputId={inputId}
        className={cx(shellClassName, inputClassName)}
        minRows={minRows}
        maxRows={maxRows}
        placeholder={placeholder}
        inputRef={inputRef}
        showValidationErrors={showValidationErrors}
        validationResult={validationResult}
        isReadOnly={isReadOnly}
        maxLength={maxLength}
        readonlyDisplayMode={readonlyDisplayMode}
        readonlyTextModeEmptyPlaceholder={readonlyTextModeEmptyPlaceholder}
        readonlyTextModeCustomClassNames={readonlyTextModeCustomClassNames}
      />
    );
  }

  return (
    <div>
      {showValidationErrors && validationResult && (
        <ValidationWarning
          isValid={validationResult.isValid}
          errors={validationResult.errors}
        />
      )}
      <div
        className={cx(
          "hidden print:block print-avoid-break",
          shellClassName,
          inputClassName,
        )}
      >
        {value ? value : ""}
      </div>
      <div
        className={cx(
          shellClassName,
          "voice-textarea-shell group/voice relative pb-10 print:hidden",
        )}
      >
        <TextareaAutosize
          id={inputId}
          onChange={handleChange}
          value={value ? value : ""}
          className={cx(
            "focus:outline-none focus:ring-0 disabled:text-gray-600",
            inputClassName,
            "block w-full resize-none bg-transparent border-0 shadow-none",
          )}
          minRows={minRows}
          maxRows={maxRows}
          placeholder={placeholder}
          ref={textareaRef}
          onBlur={handleBlur}
          maxLength={maxLength}
          onClick={syncVoiceCursor}
          onKeyUp={syncVoiceCursor}
          onFocus={syncVoiceCursor}
        />
        <SpeechToTextVoiceRow
          value={value ? value : ""}
          onChange={onChange}
          textareaRef={textareaRef}
          cursorPositionRef={voiceCursorRef}
          maxLength={maxLength}
          onCommit={onBlur}
          actionLinkClassName={actionLinkClassName}
          statusTextClassName={statusTextClassName}
          idleMicClassName={idleMicClassName}
          errorAsBadge={errorAsBadge}
          errorBadgeBackgroundColourClassName={
            errorBadgeBackgroundColourClassName
          }
          errorBadgeTextColourClassName={errorBadgeTextColourClassName}
          errorBadgeBorderRadiusClassName={errorBadgeBorderRadiusClassName}
        />
      </div>
    </div>
  );
};

export default VoiceTextArea;
